import React from "react";
import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  createRole,
  updateRole,
} from "features/RolesAndPermissions/services/RoleService";
import { Role } from "features/RolesAndPermissions/types/role.types";
import * as Yup from "yup";

interface RoleFormModalProps {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editRecord: Role | null;
}

const RoleFormModal = ({
  show,
  onClose,
  onSuccess,
  editRecord,
}: RoleFormModalProps) => {
  const isEdit = !!editRecord;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editRecord?.name ?? "",
    },
    validationSchema: Yup.object()
      .shape({
        name: Yup.string().required("Role name is required"),
      })
      .required(),
    onSubmit: async (values) => {
      try {
        if (isEdit && editRecord) {
          const response = await updateRole(Number(editRecord.id), {
            name: values.name.trim(),
          });
          if (response.success) {
            toast.success(response.message);
            onSuccess();
            onClose();
          }
        } else {
          const response = await createRole({
            name: values.name.trim(),
          });
          if (response.success) {
            toast.success(response.message);
            onSuccess();
            onClose();
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            (isEdit ? "Failed to update role" : "Failed to create role"),
        );
      }
    },
  });

  const handleClose = () => {
    validation.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit Role" : "Add Role"}
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <div className="mb-3">
            <Label className="form-label" htmlFor="role-modal-name">
              Role name
            </Label>
            <Input
              id="role-modal-name"
              type="text"
              className="form-control"
              placeholder="e.g. Manager, Viewer"
              name="name"
              value={validation.values.name}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={!!(validation.touched.name && validation.errors.name)}
            />
            {validation.touched.name && validation.errors.name && (
              <FormFeedback type="invalid">
                {validation.errors.name}
              </FormFeedback>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={validation.isSubmitting}
            >
              {validation.isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  {isEdit ? "Saving..." : "Creating..."}
                </>
              ) : (
                <>
                  <i className="ri-save-line align-bottom me-1" />
                  {isEdit ? "Save changes" : "Create role"}
                </>
              )}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default RoleFormModal;
